export interface ITxTransport {
  send(payload: string): Promise<void>;
}

export interface IRxTransport {
  onReceive(handler: (payload: string) => void): () => void;
}

export interface ImportedScan {
  EID: string;
  scanTime: string;
}

export enum Xrs2iEventType {
  SCAN = "scan",
  INVALID_EID = "invalidEid",
  ERROR = "error",
  SAVED_SCANS_PROGRESS = "savedScansProgress",
  SAVED_SCANS_COMPLETE = "savedScansComplete",
}

export interface Xrs2iScanEvent {
  type: Xrs2iEventType.SCAN;
  eid: string;
  created: string;
}

export interface Xrs2iInvalidEidEvent {
  type: Xrs2iEventType.INVALID_EID;
}

export interface Xrs2iErrorEvent {
  type: Xrs2iEventType.ERROR;
  code: string;
}

export interface Xrs2iSavedScansProgressEvent {
  type: Xrs2iEventType.SAVED_SCANS_PROGRESS;
  completedSessions: number;
  totalSessions: number | null;
  importedScans: Record<string, ReadonlyArray<ImportedScan>>;
}

export interface Xrs2iSavedScansCompleteEvent {
  type: Xrs2iEventType.SAVED_SCANS_COMPLETE;
  success: boolean;
  completedSessions: number;
  totalSessions: number | null;
  importedScans: Record<string, ReadonlyArray<ImportedScan>>;
}

export type Xrs2iEvent =
  | Xrs2iScanEvent
  | Xrs2iInvalidEidEvent
  | Xrs2iErrorEvent
  | Xrs2iSavedScansProgressEvent
  | Xrs2iSavedScansCompleteEvent;

export type Xrs2iDeviceEventHandler = (event: Xrs2iEvent) => void;

export interface Xrs2iDeviceHandle {
  resetHardwareConfig(): Promise<void>;
  readSavedScans(): Promise<void>;
  cancelReadSavedScans(): void;
  destroy(): void;
}

export enum DeviceMode {
  IDLE,
  AWAITING_EID_OUTPUT_FORMAT,
  DOWNLOADING_SAVED_SCANS,
}

export enum EIDOutputFormat {
  WITH_SPACE = "0",
  NO_SPACE = "1",
  HEX = "2",
}

export enum DownloadStep {
  WAITING_FOR_INITIAL_RECORD_RESET = "waiting_for_initial_record_reset",
  WAITING_FOR_SESSION_DATA_MODE = "waiting_for_session_data_mode",
  WAITING_FOR_SESSION_COUNT = "waiting_for_session_count",
  WAITING_FOR_SESSION_ID = "waiting_for_session_id",
  WAITING_FOR_SESSION_SELECTION = "waiting_for_session_selection",
  WAITING_FOR_SESSION_NAME = "waiting_for_session_name",
  WAITING_FOR_SESSION_RECORD_RESET = "waiting_for_session_record_reset",
  WAITING_FOR_FIELD_CODE = "waiting_for_field_code",
  WAITING_FOR_RECORD = "waiting_for_record",
}

export interface SavedSession {
  sessionId: string;
  sessionName: string;
  scans: ImportedScan[];
}

export interface DownloadState {
  step: DownloadStep;
  totalSessionCount: number | null;
  sessionIds: string[];
  currentSessionIndex: number;
  completedSessionCount: number;
  sessionsById: Record<string, SavedSession>;
}

export enum CommandResponseMode {
  NONE,
  ACK,
  DATA,
  ACK_OR_DATA,
}

export interface DeviceCommand {
  name: string;
  payload: string;
  responseMode: CommandResponseMode;
  timeoutMs: number;
  maxRetries: number;
  retryOnBusy: boolean;
}

interface FrameBase {
  raw: string;
}

export enum FrameType {
  ACK = "ack",
  ERROR = "error",
  BRACKET = "bracket",
  TEXT = "text",
}

export interface Xrs2iAckFrame extends FrameBase {
  type: FrameType.ACK;
}

export interface Xrs2iErrorFrame extends FrameBase {
  type: FrameType.ERROR;
  code: string;
}

export interface Xrs2iBracketFrame extends FrameBase {
  type: FrameType.BRACKET;
  value: string | null;
}

export interface Xrs2iTextFrame extends FrameBase {
  type: FrameType.TEXT;
  value: string;
}

export type Xrs2iFrame =
  | Xrs2iAckFrame
  | Xrs2iErrorFrame
  | Xrs2iBracketFrame
  | Xrs2iTextFrame;
